import React, { useState, useEffect, useRef } from "react";
import { replaceOrderUrl } from "../../utils/orderUrlBuilder";
import { chatApi } from "../../services/api";
import { getImageUrl } from "../../utils/imageHelper";

/**
 * CardsList Component
 * Renders a horizontal carousel of cards with loading placeholders
 * Supports "Show More" feature to load additional items from cached lookup results
 */
const CardsList = ({ cards, onButtonClick, sessionData, metadata = null, trackingSessionId = null }) => {
  const [expandedCards, setExpandedCards] = useState(cards || []);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(metadata?.items?.hasMore || false);
  const [currentOffset, setCurrentOffset] = useState(metadata?.items?.shown || (cards?.length || 0));

  // Debug logging
  useEffect(() => {
    console.log("CardsList - metadata:", metadata);
    console.log("CardsList - hasMore:", metadata?.items?.hasMore);
    console.log("CardsList - items shown:", metadata?.items?.shown);
    console.log("CardsList - items total:", metadata?.items?.total);
    console.log("CardsList - trackingSessionId:", trackingSessionId);
  }, [metadata, trackingSessionId]);

  // Sync expandedCards with cards when cards prop changes (new message).
  // Use backend metadata as source of truth so carousel limit and "see more" stay in sync.
  useEffect(() => {
    const nextCards = cards || [];
    setExpandedCards(nextCards);
    const total = metadata?.items?.total;
    const shown = metadata?.items?.shown;
    const hasMoreFromBackend = metadata?.items?.hasMore ?? false;
    setCurrentOffset(typeof shown === "number" ? shown : nextCards.length);
    // If backend says total and we already have that many cards, don't show "see more"
    if (typeof total === "number" && total > 0 && nextCards.length >= total) {
      setHasMore(false);
    } else {
      setHasMore(hasMoreFromBackend);
    }
  }, [cards, metadata]);

  if (!cards || cards.length === 0) return null;

  // Format raw item data into card format
  const formatItemToCard = (item) => {
    return {
      title: item.name || item.title || "Untitled",
      subtitle: item.description || item.subtitle || "",
      image: item.image_url || item.image || "",
      itemId: item.id,
      itemName: item.name || item.title || "Untitled",
      buttons: [
        {
          label: "Order Now",
          action: "https://cbtl.uengage.in/order",
        },
        {
          label: "More Details",
          action: `tell me more about ${item.name || item.title} [ID:${item.id}]`,
        },
      ],
    };
  };

  const handleShowMore = async () => {
    if (!trackingSessionId || isLoadingMore || !hasMore) return;

    // Use same page size as initial carousel when available (metadata.items.shown), else 10
    const pageSize = typeof metadata?.items?.shown === "number" && metadata.items.shown > 0
      ? metadata.items.shown
      : 10;

    setIsLoadingMore(true);
    try {
      const response = await chatApi.getMoreItems(trackingSessionId, currentOffset, pageSize);
      
      if (response.success && response.items) {
        // Format items into cards
        const newCards = response.items.map(formatItemToCard);
        setExpandedCards((prev) => [...prev, ...newCards]);
        setCurrentOffset(response.pagination.offset + response.pagination.limit);
        setHasMore(response.pagination.hasMore);
      }
    } catch (error) {
      console.error("Failed to load more items:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <>
      <div className={`structured-card-list ${hasMore ? 'has-show-more' : ''}`}>
        {expandedCards.map((card, index) => (
          <CardItem
            key={`${card.title || "card"}-${card.image || "no-image"}-${index}`}
            card={card}
            onButtonClick={onButtonClick}
            sessionData={sessionData}
            trackingSessionId={trackingSessionId}
          />
        ))}
        {hasMore && (
          <div className="show-more-card">
            <button
              onClick={handleShowMore}
              disabled={isLoadingMore}
              className="structured-card-button show-more-button"
            >
              {isLoadingMore ? "Loading..." : "Show More"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * Get item ID for tracking: from card.itemId or parse from "More Details" button action [ID:123]
 */
function getItemIdForTracking(card) {
  if (card.itemId != null && card.itemId !== "") {
    return Number(card.itemId);
  }
  if (!card.buttons || !Array.isArray(card.buttons)) return null;
  const moreDetailsBtn = card.buttons.find(
    (b) => b?.action && String(b.action).toLowerCase().includes("tell me") && String(b.action).includes("[ID:")
  );
  if (!moreDetailsBtn?.action) return null;
  const match = String(moreDetailsBtn.action).match(/\[ID:(\d+)\]/i);
  return match ? parseInt(match[1], 10) : null;
}

const CardItem = ({ card, onButtonClick, sessionData, trackingSessionId }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [retrySrc, setRetrySrc] = useState(null);
  const hasRetriedRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const imgRef = useRef(null);
  const imageSrc = getImageUrl(card.image);
  const effectiveSrc = retrySrc || imageSrc;
  const hasImage = imageSrc && imageSrc.trim() !== "";
  const shouldShowPlaceholder = !hasImage || imageError;

  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    setImageLoaded(false);
    setImageError(false);
    setRetrySrc(null);
    hasRetriedRef.current = false;
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [imageSrc]);

  // Handle cached images: onLoad often doesn't fire when served from cache.
  // Check img.complete && naturalWidth > 0 after mount/update.
  useEffect(() => {
    const el = imgRef.current;
    if (!el || !effectiveSrc || !hasImage || imageError) return;
    if (el.complete && el.naturalWidth > 0) {
      setImageLoaded(true);
      setImageError(false);
    }
  }, [effectiveSrc, hasImage, imageError, imageLoaded]);

  const handleImageError = () => {
    if (!hasRetriedRef.current && imageSrc) {
      hasRetriedRef.current = true;
      const sep = imageSrc.includes("?") ? "&" : "?";
      const retryUrl = `${imageSrc}${sep}t=${Date.now()}`;
      retryTimeoutRef.current = window.setTimeout(() => setRetrySrc(retryUrl), 300);
      return;
    }
    console.warn(
      "[CardItem] Image failed to load (showing placeholder):",
      card.title,
      effectiveSrc || imageSrc
    );
    setImageError(true);
    setImageLoaded(false);
  };

  return (
    <div className="structured-card">
      <div className="structured-card-image-container">
        {shouldShowPlaceholder ? (
          <div className="structured-card-image-placeholder">
            <img 
              src="/placeholderImage.png" 
              alt="Placeholder" 
              className="structured-card-image"
              style={{ objectFit: "cover" }}
            />
          </div>
        ) : (
          <>
            {!imageLoaded && !imageError && (
              <div className="structured-card-image-placeholder">
                <div className="image-loading-spinner"></div>
              </div>
            )}
            <img
              ref={imgRef}
              src={effectiveSrc}
              alt={card.title}
              className="structured-card-image"
              style={{ display: imageLoaded ? "block" : "none" }}
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
            />
          </>
        )}
      </div>

      <div className="structured-card-content">
        <h3 className="structured-card-title">{card.title}</h3>

        {card.subtitle && <p className="structured-card-subtitle">{card.subtitle}</p>}

        {card.buttons && card.buttons.length > 0 && (
          <div className="structured-card-actions">
            {card.buttons.map((button, btnIndex) => {
              // "More Details" buttons should always be rendered as buttons, not links
              const isMoreDetails = button.label?.toLowerCase().includes("more details") || 
                                    button.label?.toLowerCase().includes("details");
              
              // Check if the action is a URL (starts with http://, https://, tel:, or mailto:)
              // But exclude "More Details" buttons - they should always be buttons
              const isUrl =
                !isMoreDetails &&
                typeof button.action === "string" &&
                (button.action.startsWith("http://") || 
                 button.action.startsWith("https://") ||
                 button.action.startsWith("tel:") ||
                 button.action.startsWith("mailto:"));

              if (isUrl) {
                // Replace order URLs with dynamic ones based on session data
                const finalUrl = replaceOrderUrl(button.action, sessionData);
                const shouldOpenInNewTab = finalUrl.startsWith("http://") || finalUrl.startsWith("https://");
                const isOrderNow = String(button.label || "").toLowerCase().trim() === "order now";
                const itemIdForTracking = getItemIdForTracking(card);
                const canTrackOrder = isOrderNow && itemIdForTracking != null && !Number.isNaN(itemIdForTracking);

                // "Order Now" with itemId: button that tracks every click, then opens URL (repeat clicks counted per requirement)
                if (canTrackOrder) {
                  const handleOrderNow = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    chatApi.trackOrderClick({
                      itemId: itemIdForTracking,
                      itemName: card.itemName ?? card.title ?? "",
                      sessionId: trackingSessionId ?? undefined,
                    });
                    if (shouldOpenInNewTab) window.open(finalUrl, "_blank", "noopener,noreferrer");
                    else window.location.href = finalUrl;
                  };
                  return (
                    <button
                      key={btnIndex}
                      type="button"
                      onClick={handleOrderNow}
                      className="structured-card-button"
                    >
                      {button.label}
                    </button>
                  );
                }

                // Other URL buttons (View on Map, Call, etc.): render as link
                return (
                  <a
                    key={btnIndex}
                    href={finalUrl}
                    target={shouldOpenInNewTab ? "_blank" : "_self"}
                    rel={shouldOpenInNewTab ? "noopener noreferrer" : undefined}
                    className="structured-card-button"
                  >
                    {button.label}
                  </a>
                );
              } else {
                // Render as a button that triggers the callback
                // For "More Details" buttons, ensure we pass the card title if action is missing/invalid
                const handleClick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // If it's a "More Details" button, ensure action is in correct format
                  if (isMoreDetails) {
                    let action = button.action;
                    
                    // If action is missing, empty, or is a URL, construct it from card title
                    if (!action || 
                        action.trim() === "" || 
                        action.startsWith("http://") || 
                        action.startsWith("https://") ||
                        !action.toLowerCase().includes("tell me") && !action.toLowerCase().includes("more about")) {
                      action = `tell me more about ${card.title}`;
                    }
                    
                    onButtonClick?.({ ...button, action });
                  } else {
                    onButtonClick?.(button);
                  }
                };
                
                return (
                  <button
                    key={btnIndex}
                    type="button"
                    onClick={handleClick}
                    className="structured-card-button"
                  >
                    {button.label}
                  </button>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardsList;
